import type { Area } from "react-easy-crop";

const OUTPUT_SIZE = 512;
const OUTPUT_MIME = "image/jpeg";
const OUTPUT_QUALITY = 0.92;

const createImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });
};

const getRadianAngle = (degreeValue: number): number => {
  return (degreeValue * Math.PI) / 180;
};

const rotateSize = (width: number, height: number, rotation: number) => {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

/**
 * Crops and rotates an image to a square JPEG blob suitable for profile upload.
 */
export const getCroppedProfilePictureBlob = async (
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0,
): Promise<Blob> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("PROFILE_PICTURE_CROP_FAILED");
  }

  const rotRad = getRadianAngle(rotation);
  const { width: boundingBoxWidth, height: boundingBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation,
  );

  canvas.width = boundingBoxWidth;
  canvas.height = boundingBoxHeight;

  ctx.translate(boundingBoxWidth / 2, boundingBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");
  const croppedCtx = croppedCanvas.getContext("2d");
  if (!croppedCtx) {
    throw new Error("PROFILE_PICTURE_CROP_FAILED");
  }

  croppedCanvas.width = OUTPUT_SIZE;
  croppedCanvas.height = OUTPUT_SIZE;

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE,
  );

  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("PROFILE_PICTURE_CROP_FAILED"));
          return;
        }
        resolve(blob);
      },
      OUTPUT_MIME,
      OUTPUT_QUALITY,
    );
  });
};

export const PROFILE_PICTURE_OUTPUT_MIME = OUTPUT_MIME;
export const PROFILE_PICTURE_OUTPUT_SIZE = OUTPUT_SIZE;
